import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiErrorMessage } from "@/lib/site-audit";

type ShareResponse = { link: { token: string; expiresAt: string | null } | null };

export function ShareLinkCard({ jobId }: { jobId: string }) {
  const { toast } = useToast();
  const [noExpiry, setNoExpiry] = useState(false);
  const queryKey = [`/api/site-audit/${jobId}/share`];
  const query = useQuery<ShareResponse>({
    queryKey,
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const mint = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/site-audit/${jobId}/share`, {
        ...(noExpiry ? { expiresAt: null } : {}),
      });
      return response.json() as Promise<ShareResponse>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      toast({
        title: "Could not create the link",
        description: apiErrorMessage(error, "Try again."),
        variant: "destructive",
      });
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/site-audit/${jobId}/share`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast({ title: "Prospect link turned off" });
    },
    onError: (error) => {
      toast({
        title: "Could not turn off the link",
        description: apiErrorMessage(error, "Try again."),
        variant: "destructive",
      });
    },
  });

  const link = query.data?.link ?? null;
  const href = link ? `${window.location.origin}/audit/${link.token}` : "";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Prospect link</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          A private link to this report. It leaves out cost, re-run, history, and the paste box.
        </p>
        {link ? (
          <>
            <p className="break-all font-mono text-xs">{href}</p>
            <p className="text-xs text-muted-foreground">
              {link.expiresAt ? `Expires ${formatWhen(link.expiresAt)}.` : "Does not expire."}
              {" "}A live link is returned as it is. Turn it off to mint another.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(href).then(
                    () => toast({ title: "Link copied" }),
                    () => toast({ title: "Could not copy", description: href, variant: "destructive" }),
                  );
                }}
              >
                Copy link
              </Button>
              <Button type="button" variant="outline" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
                {revoke.isPending ? "Turning off…" : "Turn off"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={noExpiry}
                onChange={(event) => setNoExpiry(event.target.checked)}
              />
              Does not expire
            </label>
            <Button type="button" disabled={mint.isPending || query.isLoading} onClick={() => mint.mutate()}>
              {mint.isPending ? "Creating…" : "Create prospect link"}
            </Button>
            <p className="text-xs text-muted-foreground">A new link expires in 90 days unless you clear that.</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}
